import { ReactNode, useContext, useEffect, useState } from 'react';
import { createContext } from 'react';

import { EVENT_TYPES_TRANSACTIONS_VALUES, TOKENS } from '../../constants/reports/reports-filters';
import { PortfolioManagementContext } from '../portfolio-management/portfolio-management-provider';

interface ITransactionsFiltersParamsProvider {
  children: ReactNode;
}

type TypeDataType = {
  value: string;
  label: string;
  isVisible: boolean;
};

interface ITransactionsFiltersProvider {
  filters: {
    transactionsEventType: string[];
    setTransactionsEventType: React.Dispatch<React.SetStateAction<string[]>>;
    transactionsInstType: string[];
    setTransactionsInstType: React.Dispatch<React.SetStateAction<string[]>>;
    transactionsVenue: string;
    setTransactionsVenue: React.Dispatch<React.SetStateAction<string>>;
    token: string;
    tokenList: any[];
    wasCalculated: boolean;
    errorTransactions: boolean;
    setToken: React.Dispatch<React.SetStateAction<string>>;
    setTokenList: React.Dispatch<React.SetStateAction<any[]>>;
    setWasCalculated: React.Dispatch<React.SetStateAction<boolean>>;
    setErrorTransactions: React.Dispatch<React.SetStateAction<boolean>>;
    eventTypeList: TypeDataType[];
    setEventTypeList: React.Dispatch<React.SetStateAction<TypeDataType[]>>;
    instTypeList: TypeDataType[];
    setInstTypeList: React.Dispatch<React.SetStateAction<TypeDataType[]>>;
  };
}

export const VENUE_TYPE_LIST_TRANSACTIONS = [
  { value: 'deribit', label: 'Deribit', isVisible: true },
  { value: 'okx', label: 'OKX', isVisible: false },
];

export const TransactionsFiltersContext = createContext<ITransactionsFiltersProvider>(
  {} as ITransactionsFiltersProvider,
);

export const TransactionsFiltersProvider = ({ children }: ITransactionsFiltersParamsProvider) => {
  const [accountStates, setAccountStates] = useState<{ [key: string]: any }>({});
  const [transactionsEventType, setTransactionsEventType] = useState<string[]>(
    EVENT_TYPES_TRANSACTIONS_VALUES,
  );
  const [transactionsInstType, setTransactionsInstType] = useState<string[]>([]);
  const [token, setToken] = useState<string>(TOKENS[0].label);
  const [tokenList, setTokenList] = useState(TOKENS);
  const [wasCalculated, setWasCalculated] = useState(false);
  const [errorTransactions, setErrorTransactions] = useState(false);
  const [eventTypeList, setEventTypeList] = useState<TypeDataType[]>([]);
  const [instTypeList, setInstTypeList] = useState<TypeDataType[]>([]);
  const [transactionsVenue, setTransactionsVenue] = useState(VENUE_TYPE_LIST_TRANSACTIONS[0].value);
  const { selectedAccountOption } = useContext(PortfolioManagementContext);

  useEffect(() => {
    if (selectedAccountOption) {
      const accountState = accountStates[selectedAccountOption.name];
      if (accountState) {
        setTransactionsEventType(
          accountState.transactionsEventType || EVENT_TYPES_TRANSACTIONS_VALUES,
        );
        setTransactionsInstType(accountState.transactionsInstType || []);
        setTransactionsVenue(
          accountState.transactionsVenue || VENUE_TYPE_LIST_TRANSACTIONS[0].value,
        );
        setEventTypeList(accountState.eventTypeList || []);
        setInstTypeList(accountState.instTypeList || []);
        setToken(accountState.token || TOKENS[0].label);
        setTokenList(accountState.tokenList || TOKENS);
        setWasCalculated(accountState.wasCalculated || false);
        setErrorTransactions(accountState.errorTransactions || false);
      } else {
        setTransactionsEventType(EVENT_TYPES_TRANSACTIONS_VALUES);
        setTransactionsInstType([]);
        setTransactionsVenue(VENUE_TYPE_LIST_TRANSACTIONS[0].value);
        setEventTypeList([]);
        setInstTypeList([]);
        setToken(TOKENS[0].label);
        setTokenList(TOKENS);
        setWasCalculated(false);
        setErrorTransactions(false);
      }
    }
  }, [selectedAccountOption]);

  useEffect(() => {
    if (!selectedAccountOption.name) return;
    if (selectedAccountOption) {
      setAccountStates((prevState) => ({
        ...prevState,
        [selectedAccountOption.name]: {
          transactionsEventType,
          transactionsInstType,
          transactionsVenue,
          eventTypeList,
          instTypeList,
          token,
          tokenList,
          wasCalculated,
          errorTransactions,
        },
      }));
    }
  }, [
    selectedAccountOption,
    transactionsEventType,
    transactionsInstType,
    transactionsVenue,
    eventTypeList,
    instTypeList,
    token,
    tokenList,
    wasCalculated,
    errorTransactions,
  ]);

  const filters = {
    transactionsEventType,
    setTransactionsEventType,
    transactionsInstType,
    setTransactionsInstType,
    transactionsVenue,
    setTransactionsVenue,
    token,
    tokenList,
    wasCalculated,
    errorTransactions,
    setToken,
    setTokenList,
    setWasCalculated,
    setErrorTransactions,
    eventTypeList,
    setEventTypeList,
    instTypeList,
    setInstTypeList,
  };

  return (
    <TransactionsFiltersContext.Provider
      value={{
        filters,
      }}
    >
      {children}
    </TransactionsFiltersContext.Provider>
  );
};
